import React, { useState } from "react";
import { tLang } from "../../i18n";

const SUPPORTED_CALLBACK_LANGS = [
  "de",
  "en",
  "uk",
  "fr",
  "nl",
  "pl",
  "it",
  "da",
  "es",
  "no",
];

function normalizeCallbackLang(value) {
  const v = String(value || "").trim().toLowerCase();
  return SUPPORTED_CALLBACK_LANGS.includes(v) ? v : "en";
}

function normalizePhone(value) {
  const trimmed = String(value || "").trim();

  if (!trimmed) return "";

  const keepPlus = trimmed.startsWith("+") ? "+" : "";
  const digits = trimmed.replace(/\D/g, "");

  return `${keepPlus}${digits}`;
}

function isValidPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 9 && digits.length <= 15;
}

export default function AcquaintanceCallbackForm({
  apiOrigin = "",
  endpoint = "/callback-request",
  lang = "en",
}) {
  const currentLang = normalizeCallbackLang(lang);

  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const isSending = status === "sending";

  const t = (key, fallback) => tLang(key, currentLang, fallback);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const normalizedPhone = normalizePhone(phone);

    if (!isValidPhone(normalizedPhone)) {
      setStatus("error");
      setMessage(
        t(
          "front.acquaintance.callback.invalidPhone",
          "Please enter a valid phone number."
        )
      );
      return;
    }

    setStatus("sending");
    setMessage("");

    try {
      const res = await fetch(`${apiOrigin}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phone: normalizedPhone,
          page: typeof window !== "undefined" ? window.location.href : "",
          sentAt: new Date().toISOString(),
          lang: currentLang,
          source: "acquaintance-section",
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.ok === false) {
        console.warn("callback-request failed", {
          status: res.status,
          data,
        });

        throw new Error(data?.message || "Request failed");
      }

      setPhone("");
      setStatus("success");
      setMessage(
        t(
          "front.acquaintance.callback.success",
          "Request sent. I will contact you soon."
        )
      );
    } catch (err) {
      console.error("AcquaintanceCallbackForm submit error:", err);
      setStatus("error");
      setMessage(
        t(
          "front.acquaintance.callback.error",
          "Error sending request. Please try again."
        )
      );
    }
  };

  return (
    <form
      className={`acquaintance__callbackForm acquaintance__callbackForm--${status}`}
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="acquaintance__callbackInner">
        <input
          className="acquaintance__callbackInput"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder={t(
            "front.acquaintance.callback.placeholder",
            "Your phone number"
          )}
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);

            if (status === "error" || status === "success") {
              setStatus("idle");
              setMessage("");
            }
          }}
        />

        <button
          className="acquaintance__callbackButton"
          type="submit"
          disabled={isSending}
        >
          {isSending
            ? t("front.acquaintance.callback.sending", "Sending...")
            : t("front.acquaintance.callback.send", "Send")}
        </button>
      </div>

      {message && (
        <div className="acquaintance__callbackMessage">
          {message}
        </div>
      )}
    </form>
  );
}